// IndexedDB service for storing product photos offline
const DB_NAME = 'epicerie-photos';
const DB_VERSION = 1;
const STORE_NAME = 'product-photos';

class PhotoStorageService {
  constructor() {
    this.db = null;
    this.initPromise = this.initDB();
  }

  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB error:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'productId' });
        }
      };
    });
  }

  async ensureDB() {
    if (!this.db) {
      await this.initPromise;
    }
    return this.db;
  }

  // Compress image to max 200KB
  async compressImage(file, maxSizeKB = 200) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Calculate square crop (center crop)
          const size = Math.min(img.width, img.height);
          const offsetX = (img.width - size) / 2;
          const offsetY = (img.height - size) / 2;

          // Output size (max 400px for good quality thumbnails)
          const outputSize = Math.min(size, 400);
          canvas.width = outputSize;
          canvas.height = outputSize;

          // Draw cropped square image
          ctx.drawImage(
            img,
            offsetX, offsetY, size, size,
            0, 0, outputSize, outputSize
          );

          // Try different quality levels to achieve target size
          let quality = 0.8;
          let dataUrl = canvas.toDataURL('image/jpeg', quality);
          
          while (dataUrl.length > maxSizeKB * 1024 * 1.37 && quality > 0.1) {
            quality -= 0.1;
            dataUrl = canvas.toDataURL('image/jpeg', quality);
          }

          resolve(dataUrl);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  }

  // Compress from data URL
  async compressDataUrl(dataUrl, maxSizeKB = 200) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Calculate square crop
        const size = Math.min(img.width, img.height);
        const offsetX = (img.width - size) / 2;
        const offsetY = (img.height - size) / 2;

        // Output size
        const outputSize = Math.min(size, 400);
        canvas.width = outputSize;
        canvas.height = outputSize;

        ctx.drawImage(
          img,
          offsetX, offsetY, size, size,
          0, 0, outputSize, outputSize
        );

        let quality = 0.8;
        let result = canvas.toDataURL('image/jpeg', quality);
        
        while (result.length > maxSizeKB * 1024 * 1.37 && quality > 0.1) {
          quality -= 0.1;
          result = canvas.toDataURL('image/jpeg', quality);
        }

        resolve(result);
      };
      img.src = dataUrl;
    });
  }

  // Save photo for a product
  async savePhoto(productId, photoDataUrl) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.put({
        productId,
        photo: photoDataUrl,
        updatedAt: new Date().toISOString()
      });

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // Get photo for a product
  async getPhoto(productId) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(productId);

      request.onsuccess = () => {
        resolve(request.result?.photo || null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Get multiple photos at once
  async getPhotos(productIds) {
    const db = await this.ensureDB();
    const photos = {};
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      
      let completed = 0;
      
      productIds.forEach((productId) => {
        const request = store.get(productId);
        request.onsuccess = () => {
          if (request.result?.photo) {
            photos[productId] = request.result.photo;
          }
          completed++;
          if (completed === productIds.length) {
            resolve(photos);
          }
        };
        request.onerror = () => {
          completed++;
          if (completed === productIds.length) {
            resolve(photos);
          }
        };
      });

      if (productIds.length === 0) {
        resolve(photos);
      }
    });
  }

  // Delete photo for a product
  async deletePhoto(productId) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(productId);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // Get all photos
  async getAllPhotos() {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const photos = {};
        request.result.forEach(item => {
          photos[item.productId] = item.photo;
        });
        resolve(photos);
      };
      request.onerror = () => reject(request.error);
    });
  }
}

// Singleton instance
export const photoStorage = new PhotoStorageService();

// Category icons for products without photos
// These are fallback icons when category doesn't have a custom icon
export const CATEGORY_ICONS = {
  // Fashion categories
  'Robes': '👗',
  'Hauts / Tops': '👕',
  'Pantalons / Jeans': '👖',
  'Vestes / Manteaux': '🧥',
  'Chaussures': '👠',
  'Accessoires': '👜',
  'Sous-vêtements / Lingerie': '👙',
  'Enfants': '👶',
  // Legacy food categories (for backwards compatibility)
  'Fruits': '🍌',
  'Légumes': '🥬',
  'Épices': '🌶️',
  'Boissons': '🥤',
  'Conserves': '🥫',
  // Caisse universelle
  'Plats': '🍽️', 'Entrées': '🥗', 'Desserts': '🍰', 'Menus': '📋', 'Accompagnements': '🍟',
  'Boissons chaudes': '☕', 'Cocktails': '🍸', 'Softs': '🥤', 'Cafés & Thés': '☕', 'Snacks': '🥨',
  'Bières': '🍺', 'Vins': '🍷', 'Frais': '🥬', 'Fruits & Légumes': '🍎', 'Épicerie sèche': '🍚',
  'Boulangerie': '🥖', 'Hygiène & Entretien': '🧼', 'Surgelés': '🧊', 'Articles': '📦', 'Services': '🛠️', 'Divers': '📦',
  'default': '👗'
};

export const getCategoryIcon = (categoryName, customIcon = null) => {
  // Use custom icon if provided
  if (customIcon) return customIcon;
  // Fallback to predefined icons
  if (CATEGORY_ICONS[categoryName]) return CATEGORY_ICONS[categoryName];
  try {
    const b = JSON.parse(localStorage.getItem("boutique") || "{}");
    const t = b.type || b.boutique_type;
    if (b.profile === "caisse" || ["restaurant", "bar", "epicerie", "boissons", "general"].includes(t)) return '📦';
  } catch {}
  return CATEGORY_ICONS.default;
};
